import { ROTULO_DO_TIPO_DE_MIDIA } from '@/shared/constants';
import type { OutboundMediaTipo } from '@/shared/types/midiaDeSaida';
import { isRecord } from '@/shared/utils/apiResponse';

import type {
  EstadoSimulacao,
  EventoSimulacao,
  SimulacaoFormField,
  SimulacaoInteraction,
  SimulacaoLink,
  SimulacaoMessage,
  SimulacaoMessageContent as Conteudo,
  PayloadDoEvento as Payload,
  RotaDaAssinatura,
  SituacaoDaConversa,
  SimulacaoReplyOption,
  SimulacaoReplySection,
  TipoDeEventoConhecido,
} from '../types';
import { ehUnidadeDeAvanco } from './avancoDoRelogio';
import { classificarLink, extrairLinks } from './classificarLink';
import {
  rotuloDoAutor,
  rotuloDoEstado,
  TEXTO_DO_DIRECIONAMENTO,
  TEXTO_DO_EMAIL,
  textoDaFalha,
  textoDaSessao,
  textoDoAtendimentoHumano,
  textoDoEncerramento,
  textoDoEncerramentoDoCaso,
  textoDoEventoDesconhecido,
  textoDoTempoAvancado,
} from './rotulosDaSimulacao';

const texto = (payload: Payload, chave: string): string | null => {
  const valor = payload[chave];
  return typeof valor === 'string' ? valor : null;
};

const lista = (payload: Payload, chave: string): Payload[] => {
  const valor = payload[chave];
  return Array.isArray(valor) ? valor.filter(isRecord) : [];
};

const opcao = (item: Payload): SimulacaoReplyOption | null => {
  const id = texto(item, 'id');
  const label = texto(item, 'title') ?? texto(item, 'label');
  if (!id || !label) return null;
  const description = texto(item, 'description') ?? undefined;
  return description ? { id, label, description } : { id, label };
};

const opcoes = (itens: Payload[]): SimulacaoReplyOption[] =>
  itens.map(opcao).filter((item): item is SimulacaoReplyOption => item !== null);

const linkDoTexto = (url: string): SimulacaoLink => ({ label: url, target: classificarLink(url) });

/**
 * O veredito do core só pode tornar o link mais restrito: o `seguro: false` dele vale, e o
 * `seguro: true` ainda passa pela regra do web, que é a que decide o que a tela abre (CA-18).
 */
const linkDoCore = (item: Payload): SimulacaoLink | null => {
  const url = texto(item, 'url');
  if (!url) return null;
  const link = linkDoTexto(url);
  return item.seguro === true ? link : { ...link, target: { safe: false } };
};

/** Os `links` do core quando vierem; sem eles, os que o web acha no texto. */
const linksDaMensagem = (payload: Payload, conteudo: string): SimulacaoLink[] =>
  Array.isArray(payload.links)
    ? lista(payload, 'links')
        .map(linkDoCore)
        .filter((link): link is SimulacaoLink => link !== null)
    : extrairLinks(conteudo).map(linkDoTexto);

const comRodape = (corpo: string, payload: Payload) => {
  const rodape = texto(payload, 'footerText');
  return rodape ? `${corpo}\n${rodape}` : corpo;
};

const secoes = (interativo: Payload): SimulacaoReplySection[] =>
  lista(interativo, 'sections').map((secao) => ({
    title: texto(secao, 'title') ?? undefined,
    options: opcoes(lista(secao, 'rows')),
  }));

const interacao = (messageId: string, interativo: Payload): SimulacaoInteraction | null => {
  const tipo = texto(interativo, 'kind');
  if (tipo === 'buttons') {
    return { kind: 'buttons', messageId, options: opcoes(lista(interativo, 'buttons')) };
  }
  if (tipo === 'list') return { kind: 'list', messageId, sections: secoes(interativo) };
  if (tipo === 'urlButton') {
    const label = texto(interativo, 'displayText') ?? 'Abrir link';
    return {
      kind: 'urlButton',
      messageId,
      urlButton: { label, target: classificarLink(texto(interativo, 'url')) },
    };
  }
  return null;
};

const conteudoInterativo = (payload: Payload): Conteudo | null => {
  const messageId = texto(payload, 'mensagemId');
  const interativo = payload.interativo;
  if (!messageId || !isRecord(interativo)) return null;
  const interaction = interacao(messageId, interativo);
  if (!interaction) return null;
  const corpo = comRodape(texto(interativo, 'bodyText') ?? '', interativo);
  return { kind: 'interactive', content: corpo, interaction };
};

const TIPOS_DE_CAMPO = new Set(['text', 'number', 'date', 'select']);

const campo = (item: Payload): SimulacaoFormField | null => {
  const id = texto(item, 'id');
  const label = texto(item, 'label');
  const tipo = texto(item, 'tipo');
  if (!id || !label || !tipo || !TIPOS_DE_CAMPO.has(tipo)) return null;
  return {
    id,
    label,
    type: tipo === 'number' || tipo === 'date' || tipo === 'select' ? tipo : 'text',
    required: item.obrigatorio === true,
    options: opcoes(lista(item, 'opcoes')),
  };
};

const conteudoDeFormulario = (payload: Payload): Conteudo | null => {
  const messageId = texto(payload, 'mensagemId');
  const fields = lista(payload, 'campos')
    .map(campo)
    .filter((item): item is SimulacaoFormField => item !== null);
  if (!messageId || fields.length === 0) return null;
  return {
    kind: 'interactive',
    content: texto(payload, 'texto') ?? '',
    interaction: { kind: 'form', messageId, fields },
  };
};

/** O Record de rótulos é completo por tipo: tipo novo na união já entra aqui sem outra lista. */
const ehTipoDeMidia = (valor: string | null): valor is OutboundMediaTipo =>
  valor !== null && Object.hasOwn(ROTULO_DO_TIPO_DE_MIDIA, valor);

const ehTipoConhecido = (tipo: string): tipo is TipoDeEventoConhecido =>
  Object.hasOwn(LEITORES, tipo);

const conteudoDeMidia = (payload: Payload): Conteudo | null => {
  const name = texto(payload, 'nomeArquivo');
  const mediaType = texto(payload, 'tipoMidia');
  if (!name || !ehTipoDeMidia(mediaType)) return null;
  return {
    kind: 'file',
    content: texto(payload, 'legenda') ?? '',
    file: { name, fileId: texto(payload, 'arquivoId'), mediaType },
  };
};

const PREFIXO_DA_ROTA_DA_ASSINATURA = '/negociacoes/simulacao/assinatura/';

/** A tela de assinatura é rota interna (TELA-002): o link não passa pela regra de link externo. */
export const rotaDaAssinaturaDoEscritorio: RotaDaAssinatura = (assinaturaId) =>
  `${PREFIXO_DA_ROTA_DA_ASSINATURA}${encodeURIComponent(assinaturaId)}`;

const conteudoDeAssinatura = (
  payload: Payload,
  rotaDaAssinatura: RotaDaAssinatura,
): Conteudo | null => {
  const assinaturaId = texto(payload, 'assinaturaId');
  const label = texto(payload, 'nomeDocumento');
  if (!label) return null;
  return {
    kind: 'signature',
    content: '',
    link: {
      label,
      target: assinaturaId ? { safe: true, href: rotaDaAssinatura(assinaturaId) } : null,
    },
  };
};

const conteudoDeTexto = (payload: Payload): Conteudo | null => {
  const content = texto(payload, 'texto');
  if (content === null) return null;
  return { kind: 'text', content, links: linksDaMensagem(payload, content) };
};

const conteudoDoTempo = (payload: Payload): Conteudo => {
  const { quantidade } = payload;
  const unidade = texto(payload, 'unidade');
  if (typeof quantidade !== 'number' || unidade === null || !ehUnidadeDeAvanco(unidade)) {
    return { kind: 'session', content: 'Tempo do teste avançado.' };
  }
  return { kind: 'session', content: textoDoTempoAvancado(quantidade, unidade) };
};

const conteudoDaSessao = (payload: Payload): Conteudo | null => {
  const situacao = texto(payload, 'situacao');
  if (situacao !== 'ABERTA' && situacao !== 'EXPIRADA') return null;
  return { kind: 'session', content: textoDaSessao(situacao) };
};

const conteudoDeTemplate = (payload: Payload): Conteudo | null => {
  const content = texto(payload, 'texto');
  if (content === null) return null;
  return { kind: 'template', content, links: linksDaMensagem(payload, content) };
};

const eventoDeSistema = (content: string) => (): Conteudo => ({ kind: 'session', content });

const conteudoDoAtendimentoHumano = (payload: Payload): Conteudo => ({
  kind: 'session',
  content: textoDoAtendimentoHumano(texto(payload, 'motivo')),
});

const MOTIVO_DO_CASO_ENCERRADO = 'CASO_ENCERRADO';

const conteudoDoEncerramento = (payload: Payload): Conteudo => {
  const motivo = texto(payload, 'motivo');
  return {
    kind: 'session',
    content:
      motivo === MOTIVO_DO_CASO_ENCERRADO
        ? textoDoEncerramentoDoCaso(texto(payload, 'status'), texto(payload, 'nota'))
        : textoDoEncerramento(motivo),
  };
};

const conteudoDaFalha = (payload: Payload): Conteudo => ({
  kind: 'session',
  content: textoDaFalha(texto(payload, 'codigo')),
});

const LEITORES: Record<
  TipoDeEventoConhecido,
  (payload: Payload, rotaDaAssinatura: RotaDaAssinatura) => Conteudo | null
> = {
  TEXTO: conteudoDeTexto,
  TEMPLATE: conteudoDeTemplate,
  INTERATIVO: conteudoInterativo,
  FORMULARIO: conteudoDeFormulario,
  MIDIA: conteudoDeMidia,
  SESSAO: conteudoDaSessao,
  ASSINATURA: conteudoDeAssinatura,
  DIRECIONAMENTO: eventoDeSistema(TEXTO_DO_DIRECIONAMENTO),
  ATENDIMENTO_HUMANO: conteudoDoAtendimentoHumano,
  TEMPO_AVANCADO: conteudoDoTempo,
  ENCERRAMENTO: conteudoDoEncerramento,
  FALHA: conteudoDaFalha,
  EMAIL: eventoDeSistema(TEXTO_DO_EMAIL),
};

/**
 * O evento da decisão da assinatura vem sem `estado` e com o `resultado` no payload: é ele que
 * diz, no balão e na exportação, como a etapa do provedor terminou.
 */
const estadoDoEvento = (evento: EventoSimulacao): string | null =>
  evento.estado ?? (evento.tipo === 'ASSINATURA' ? texto(evento.payload, 'resultado') : null);

/**
 * Evento do core para o balão da conversa. Tipo desconhecido ou payload fora do formato vira o
 * card genérico: o histórico mostra que algo aconteceu, em vez de engolir o evento (CA-11). A tela
 * de assinatura é a da superfície da conversa: a do cliente ou a do admin (CA-27).
 */
export const mensagemDoEvento = (
  evento: EventoSimulacao,
  rotaDaAssinatura: RotaDaAssinatura = rotaDaAssinaturaDoEscritorio,
): SimulacaoMessage => {
  const conteudo = (ehTipoConhecido(evento.tipo) &&
    LEITORES[evento.tipo](evento.payload, rotaDaAssinatura)) || {
    kind: 'unknown' as const,
    content: textoDoEventoDesconhecido(evento.tipo),
  };
  return {
    id: evento.id,
    role: evento.autor === 'CLIENTE' ? 'client' : 'agent',
    author: rotuloDoAutor(evento.autor),
    createdAt: evento.ocorridoEm,
    status: rotuloDoEstado(estadoDoEvento(evento)),
    ...conteudo,
  };
};

/** O link que o core gera tem um destes caminhos: o do cliente ou o do admin, com o fluxo inicial. */
const ASSINATURA_NOS_CAMINHOS = [
  new RegExp(`^${PREFIXO_DA_ROTA_DA_ASSINATURA}([0-9a-f-]{36})(?:/|$)`, 'i'),
  /^\/admin-geral\/flows\/simulacao\/[^/]+\/assinatura\/([0-9a-f-]{36})(?:\/|$)/i,
];

const linksExibidos = (mensagem: SimulacaoMessage): SimulacaoLink[] => {
  if (mensagem.kind === 'text' || mensagem.kind === 'template') return mensagem.links;
  if (mensagem.kind === 'interactive' && mensagem.interaction.urlButton) {
    return [mensagem.interaction.urlButton];
  }
  return [];
};

/**
 * Só o link que a bolha abre conta: o "Link inseguro" (http, `seguro: false` do core), o texto sem
 * esquema e a URL que o botão de resposta carrega sem mostrar não levam ninguém ao documento, e
 * esconder o card nesses casos deixaria a conversa sem acesso a ele.
 */
const linksQueAMensagemAbre = (mensagem: SimulacaoMessage): string[] =>
  linksExibidos(mensagem).flatMap(({ target }) => (target?.safe ? [target.href] : []));

const assinaturaDoLink = (href: string): string | null => {
  const { pathname } = new URL(href);
  const id = ASSINATURA_NOS_CAMINHOS.map((caminho) => caminho.exec(pathname)?.[1]).find(Boolean);
  return id?.toLowerCase() ?? null;
};

/** Assinaturas de teste cujo link o próprio agente mandou ao cliente (texto, template ou botão). */
const assinaturasEnviadasPeloAgente = (eventos: EventoSimulacao[]): Set<string> =>
  new Set(
    eventos
      .filter((evento) => evento.autor === 'AGENTE')
      .flatMap((evento) => linksQueAMensagemAbre(mensagemDoEvento(evento)))
      .map(assinaturaDoLink)
      .filter((id): id is string => id !== null),
  );

/**
 * O card da criação existe para o caso em que o flow não manda o link ao cliente (o provedor é
 * que avisaria por e-mail). Quando o agente manda, o card repetia o mesmo documento na conversa:
 * a pessoa via dois envios onde a produção tem um. A decisão da assinatura (com `resultado`)
 * continua no histórico.
 */
const ehCriacaoJaEnviadaPeloAgente = (evento: EventoSimulacao, enviadas: Set<string>): boolean => {
  if (evento.tipo !== 'ASSINATURA' || texto(evento.payload, 'resultado') !== null) return false;
  const assinaturaId = texto(evento.payload, 'assinaturaId');
  return assinaturaId !== null && enviadas.has(assinaturaId.toLowerCase());
};

export const mensagensDosEventos = (
  eventos: EventoSimulacao[],
  rotaDaAssinatura: RotaDaAssinatura = rotaDaAssinaturaDoEscritorio,
): SimulacaoMessage[] => {
  const enviadas = assinaturasEnviadasPeloAgente(eventos);
  return eventos
    .filter((evento) => !ehCriacaoJaEnviadaPeloAgente(evento, enviadas))
    .map((evento) => mensagemDoEvento(evento, rotaDaAssinatura));
};

/** Ids de mensagem interativa que o cliente já respondeu: elas ficam desabilitadas (CA-12). */
export const mensagensRespondidas = (eventos: EventoSimulacao[]): string[] =>
  eventos.flatMap((evento) => {
    const resposta = evento.autor === 'CLIENTE' ? evento.payload.respostaDe : null;
    return isRecord(resposta) && typeof resposta.mensagemId === 'string'
      ? [resposta.mensagemId]
      : [];
  });

/**
 * Direcionamento e atendimento humano não voltam sozinhos na simulação (decisão 11 do plano):
 * o último dos dois que chegou define a situação até a conversa acabar.
 */
export const situacaoDaConversa = (
  simulacao: EstadoSimulacao,
  eventos: EventoSimulacao[],
): SituacaoDaConversa => {
  if (simulacao.status === 'ENCERRADA') return 'ENCERRADA';
  const pausa = eventos.findLast(
    (evento) => evento.tipo === 'DIRECIONAMENTO' || evento.tipo === 'ATENDIMENTO_HUMANO',
  );
  if (pausa?.tipo === 'DIRECIONAMENTO') return 'AGUARDANDO_DIRECIONAMENTO';
  if (pausa?.tipo === 'ATENDIMENTO_HUMANO') return 'EM_ATENDIMENTO_HUMANO';
  return 'ATIVA';
};
